import { createContext, useMemo, useState } from "react";

import {
	enUS as coreEnUS,
	nbNO as coreNbNO,
	ptBR as corePtBR,
} from "@mui/material/locale";
import { createTheme } from "@mui/material/styles";
import { enUS, nbNO, ptBR } from "@mui/x-data-grid/locales";
import {
	enUS as pickersEnUS,
	nbNO as pickersNbNO,
	ptBR as pickersPtBR,
} from "@mui/x-date-pickers/locales";

import { useFetchActiveUser } from "@/service/ApplicationQueries";

// color design tokens export
type Mode = "light" | "dark";

type ColorTokens = {
	[key: string]: {
		[key: number]: string;
		main?: string;
	};
};

export const tokens = (mode: Mode): ColorTokens => {
	const inverted = (colors: {
		[x: string]: any;
		[x: number]: string;
		main?: string | undefined;
	}) => {
		const keys = Object.keys(colors);
		return keys.reduce((acc: { [key: string]: string }, key) => {
			acc[key] = colors[keys[keys.length - 1 - keys.indexOf(key)]];
			return acc;
		}, {});
	};

	const baseTokens: ColorTokens = {
		grey: {
			100: "#ffffff",
			200: "#f2f2f2",
			300: "#a3a3a3",
			400: "#858585",
			500: "#666666",
			600: "#525252",
			700: "#3d3d3d",
			800: "#292929",
			900: "#141414",
		},
		primary: {
			100: "#d0d1d5",
			200: "#a1a4ab",
			300: "#727681",
			400: "#1F2A40",
			500: "#141b2d",
			600: "#101624",
			700: "#0c101b",
			800: "#080b12",
			900: "#040509",
		},
		greenAccent: {
			main: "#4cceac",
			100: "#dbf5ee",
			200: "#b7ebde",
			300: "#94e2cd",
			400: "#70d8bd",
			500: "#4cceac",
			600: "#3da58a",
			700: "#2e7c67",
			800: "#1e5245",
			900: "#0f2922",
		},
		redAccent: {
			100: "#f8dcdb",
			200: "#f1b9b7",
			300: "#e99592",
			400: "#e2726e",
			500: "#db4f4a",
			600: "#af3f3b",
			700: "#832f2c",
			800: "#58201e",
			900: "#2c100f",
		},
		blueAccent: {
			100: "#c2dfff",
			200: "#94c2ff",
			300: "#66a6ff",
			400: "#3d8eff",
			500: "#2555d4",
			600: "#0060cc",
			700: "#0050a3",
			800: "#004072",
			900: "#002641",
		},
		yellowAccent: {
			100: "#FFF9C4",
			200: "#FFF176",
			300: "#FFEB3B",
			400: "#FDD835",
			500: "#FBC02D",
			600: "#F9A825",
			700: "#F57F17",
			800: "#E65100",
			900: "#FF6F00",
		},
	};

	return mode === "dark"
		? baseTokens
		: Object.fromEntries(
				Object.entries(baseTokens).map(([key, value]) => [
					key,
					inverted(value),
				]),
			);
};

// mui theme settings
export const themeSettings = (mode: Mode): object => {
	const colors = tokens(mode);
	return {
		overrides: {
			MuiStepIcon: {
				root: {
					"&$completed": {
						color: "pink",
					},
					"&$active": {
						color: "red",
					},
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					root: {
						".even-row": {
							backgroundColor: colors.redAccent[200],
						},
					},
				},
			},
		},
		palette: {
			mode: mode,
			...(mode === "dark"
				? {
						// palette values for dark mode
						primary: {
							main: colors.primary[500],
						},
						secondary: {
							main: colors.greenAccent[400],
						},
						info: {
							main: colors.blueAccent[400],
						},
						error: {
							main: colors.redAccent[400],
						},
						neutral: {
							dark: colors.grey[700],
							main: colors.grey[500],
							light: colors.grey[100],
						},
						background: {
							default: colors.primary[500],
						},
					}
				: {
						// palette values for light mode
						primary: {
							main: colors.primary[500],
						},
						secondary: {
							main: colors.greenAccent[400],
						},
						info: {
							main: colors.blueAccent[400],
						},
						error: {
							main: colors.redAccent[400],
						},
						neutral: {
							dark: colors.grey[700],
							main: colors.grey[400],
							light: colors.grey[100],
						},
						background: {
							default: "#fcfcfc",
						},
					}),
		},
		typography: {
			fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
			fontSize: 14,
			h1: {
				fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
				fontSize: 40,
			},
			h2: {
				fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
				fontSize: 32,
			},
			h3: {
				fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
				fontSize: 24,
			},
			h4: {
				fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
				fontSize: 20,
			},
			h5: {
				fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
				fontSize: 16,
			},
			h6: {
				fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
				fontSize: 14,
			},
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						backgroundColor: (theme: {
							palette: {
								mode: string;
								background: { default: any; paper: any };
							};
						}) =>
							theme.palette.mode === "dark"
								? theme.palette.background.default
								: theme.palette.background.paper,
						color: (theme: {
							palette: { mode: string; text: { primary: any; secondary: any } };
						}) =>
							theme.palette.mode === "dark"
								? theme.palette.text.primary
								: theme.palette.text.secondary,
					},
					html: {
						backgroundColor: (theme: { palette: { mode: string } }) =>
							theme.palette.mode === "dark" ? "red" : "blue",
					},
				},
			},
			MuiDrawer: {
				styleOverrides: {
					paper: {
						backgroundColor: (theme: { palette: { primary: { main: any } } }) =>
							theme.palette.primary.main,
						boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
						border: "none",
						padding: "0px 16px",
					},
					root: {
						"& .Mui-selected": {
							backgroundColor: "#E3F2FD",
							color: "#1976D2",
							"&:hover": {
								backgroundColor: "#BBDEFB",
							},
						},
						"& .MuiListItemIcon-root": {
							color: "#1976D2", // Modern blue icons
						},
					},
				},
			},
			MuiAccordionSummary: {
				styleOverrides: {
					content: {
						overflow: "hidden",
					},
				},
			},
			MuiStepIcon: {
				styleOverrides: {
					root: {
						"&.Mui-active": {
							color: colors.blueAccent[400],
							"&.Mui-error": {
								color: colors.redAccent[400],
							},
						},
						"&.Mui-completed": {
							color: colors.greenAccent[400],
						},
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					text: {
						color: (theme: {
							palette: { mode: string; primary: { main: any } };
						}) =>
							theme.palette.mode === "dark"
								? "red"
								: theme.palette.primary.main,
					},
				},
			},

			// Override for MuiTabs
			MuiTabs: {
				styleOverrides: {
					root: {
						borderBottom: "none",
					},
				},
			},
			MuiCheckbox: {
				styleOverrides: {
					root: {
						color: mode === "dark" ? colors.grey[100] : colors.grey[100],
						"&.Mui-checked": {
							color:
								mode === "dark" ? colors.greenAccent[400] : colors.primary[500],
						},
					},
				},
			},
			MuiTypography: {
				styleOverrides: {
					root: {
						color: colors.grey[100],
					},
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					root: {
						border: "none",
						backgroundColor: (theme: {
							palette: { mode: string; background: { default: any } };
						}) =>
							theme.palette.mode === "dark"
								? theme.palette.background.default
								: "#fff",
					},
					cell: {
						color: (theme: {
							palette: { mode: string; text: { primary: any; secondary: any } };
						}) =>
							theme.palette.mode === "dark"
								? theme.palette.text.primary
								: theme.palette.text.secondary,
						borderBottom: (theme: {
							palette: { mode: string; divider: any };
						}) =>
							theme.palette.mode === "dark"
								? `1px solid ${theme.palette.divider}`
								: "1px solid #e0e0e0",
					},
					columnHeaders: {
						backgroundColor: (theme: {
							palette: { mode: string; grey: any[] };
						}) =>
							theme.palette.mode === "dark"
								? theme.palette.grey[800]
								: theme.palette.grey[200],
						color: (theme: {
							palette: { mode: string; text: { primary: any; secondary: any } };
						}) =>
							theme.palette.mode === "dark"
								? theme.palette.text.primary
								: theme.palette.text.secondary,
						borderBottom: (theme: {
							palette: { mode: string; divider: any };
						}) =>
							theme.palette.mode === "dark"
								? `1px solid ${theme.palette.divider}`
								: "1px solid #e0e0e0",
						fontWeight: 600,
					},
					row: {
						"&:nth-of-type(odd)": {
							backgroundColor: (theme: {
								palette: { mode: string; grey: any[] };
							}) =>
								theme.palette.mode === "dark"
									? theme.palette.grey[900]
									: theme.palette.grey[100],
						},
						"&:nth-of-type(even)": {
							backgroundColor: (theme: {
								palette: { mode: string; grey: any[] };
							}) =>
								theme.palette.mode === "dark"
									? theme.palette.grey[800]
									: theme.palette.grey[50],
						},
						"&:hover": {
							backgroundColor: (theme: {
								palette: {
									mode: string;
									action: { hover: any; selected: any };
								};
							}) =>
								theme.palette.mode === "dark"
									? theme.palette.action.hover
									: theme.palette.action.selected,
						},
					},
					toolbar: {
						backgroundColor: (theme: {
							palette: { mode: string; grey: any[] };
						}) =>
							theme.palette.mode === "dark"
								? theme.palette.grey[800]
								: theme.palette.grey[100],
						color: (theme: {
							palette: { mode: string; text: { primary: any; secondary: any } };
						}) =>
							theme.palette.mode === "dark"
								? theme.palette.text.primary
								: theme.palette.text.secondary,
					},
				},
			},
		},
	};
};

// context for color mode
export const ColorModeContext = createContext({
	toggleColorMode: () => {},
	mode: "light", // Default value
});

export const useMode = () => {
	const [mode, setMode] = useState<Mode>("light");
	const { data: user } = useFetchActiveUser(true);

	// Toggle color mode function
	const toggleColorMode = () => {
		setMode((prev) => (prev === "light" ? "dark" : "light"));
	};

	// Localization based on backend language settings
	const locale = useMemo(
		() => ({
			dataGrid:
				user?.Company?.Language.LanguageString === "no"
					? nbNO
					: user?.Company?.Language.LanguageString === "pt"
						? ptBR
						: enUS,
			datePickers:
				user?.Company?.Language.LanguageString === "no"
					? pickersNbNO
					: user?.Company?.Language.LanguageString === "pt"
						? pickersPtBR
						: pickersEnUS,
			charts:
				user?.Company?.Language.LanguageString === "no"
					? nbNO
					: user?.Company?.Language.LanguageString === "pt"
						? ptBR
						: enUS,
			core:
				user?.Company?.Language.LanguageString === "no"
					? coreNbNO
					: user?.Company?.Language.LanguageString === "pt"
						? corePtBR
						: coreEnUS,
		}),
		[user?.Company?.Language],
	);

	// Dynamic theme creation
	const theme = useMemo(
		() =>
			createTheme(
				themeSettings(mode),
				locale.dataGrid,
				locale.datePickers,
				locale.core,
				locale.charts,
			),
		[mode, locale],
	);

	return { theme, colorMode: { toggleColorMode, mode } };
};
